import Flex from '@Components/Bases/Flex';
import FormCheckbox from '@Components/Form/FormCheckBox';
import { defaultCompletedSection } from '@Domains/utils';
import { Divider, Typography } from '@mui/material';
import { formatToShortThaiDateTime } from '@Utils/datetime';
import { resetCompletedSection } from '@Utils/updateCompletedSection';
import { useFormContext } from 'react-hook-form';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { useGreenhouseForm } from '../EsgGreenhouseFormProvider';
import { IClimateChangeStructureFormValues } from '../EsgGreenhouseFormProvider/types';
import {
  checkBoardResponsibilityUrlValid,
  checkClimateChangeBoardResponsibilityEnValid,
  checkClimateChangeComplete,
  checkClimateChangeCompleteEnValid,
  checkClimateChangeSubcommitteeResponsibilityEnValid,
  checkClimateChangeSubcommitteeResponsibilityRemarkSpaceValid,
  checkClimateChangeTermEnValid,
  checkClimateChangeValidateSpace,
  checkClimateChangeValidateSpaceIntro,
  checkClimateChangeValidateSpaceRemark,
  checkHasClimateChangeValid,
  checkSubcommitteesComplete,
} from './utils';

const ClimateChangeStructureCompletedCheckbox = () => {
  const { watch } = useGreenhouseForm();
  const { isPublished } = useProgressBar();
  const climateChangeStructure = watch(
    'climateChangeStructure'
  ) as IClimateChangeStructureFormValues;
  const { setValue } = useFormContext();

  const { boardResponsibility, subcommitteeResponsibility } =
    climateChangeStructure || {};

  const completedSection =
    climateChangeStructure?.completedSection || defaultCompletedSection;
  const {
    isCompletedTh,
    isCompletedEn,
    dateTh,
    dateEn,
    roleStampTh,
    roleStampEn,
  } = completedSection;

  const formattedCompletedSectionDateTh = dateTh
    ? formatToShortThaiDateTime(dateTh)
    : '';
  const formattedCompletedSectionDateEn = dateEn
    ? formatToShortThaiDateTime(dateEn)
    : '';

  const isCompleteTh =
    checkClimateChangeComplete(
      climateChangeStructure as IClimateChangeStructureFormValues,
      'Th'
    ) &&
    checkClimateChangeValidateSpace(climateChangeStructure, 'Th') &&
    checkBoardResponsibilityUrlValid(boardResponsibility, 'Th') &&
    checkHasClimateChangeValid(climateChangeStructure) &&
    checkClimateChangeValidateSpaceIntro(climateChangeStructure, 'Th') &&
    checkClimateChangeValidateSpaceRemark(climateChangeStructure, 'Th') &&
    checkClimateChangeSubcommitteeResponsibilityRemarkSpaceValid(
      subcommitteeResponsibility,
      'Th'
    ) &&
    checkSubcommitteesComplete(subcommitteeResponsibility?.subcommittees, 'Th');

  const isCompleteEn =
    checkClimateChangeComplete(
      climateChangeStructure as IClimateChangeStructureFormValues,
      'Th'
    ) &&
    checkClimateChangeCompleteEnValid(climateChangeStructure) &&
    checkClimateChangeTermEnValid(climateChangeStructure) &&
    checkClimateChangeBoardResponsibilityEnValid(boardResponsibility) &&
    checkClimateChangeSubcommitteeResponsibilityEnValid(
      subcommitteeResponsibility
    ) &&
    checkBoardResponsibilityUrlValid(boardResponsibility, 'En') &&
    checkHasClimateChangeValid(climateChangeStructure) &&
    checkClimateChangeValidateSpace(climateChangeStructure, 'En') &&
    checkClimateChangeValidateSpaceIntro(climateChangeStructure, 'En') &&
    checkClimateChangeValidateSpaceRemark(climateChangeStructure, 'En') &&
    checkClimateChangeSubcommitteeResponsibilityRemarkSpaceValid(
      subcommitteeResponsibility,
      'En'
    ) &&
    checkSubcommitteesComplete(subcommitteeResponsibility?.subcommittees, 'En');

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    language: 'Th' | 'En'
  ) => {
    const updatedSection = { ...completedSection };

    if (language === 'Th') {
      updatedSection.isCompletedTh = e.target.checked;
    } else {
      updatedSection.isCompletedEn = e.target.checked;
    }

    resetCompletedSection(updatedSection, setValue, 'climateChangeStructure');
  };

  return (
    <>
      <Divider />
      <Flex gap={4} justifyContent={'center'} bgcolor={'grey.50'} py={1}>
        <Flex alignItems={'center'}>
          <Typography fontWeight={800} textAlign={'right'}>
            ยืนยันการตรวจสอบข้อมูลครบถ้วน
          </Typography>
        </Flex>
        <Flex>
          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              name="climateChangeStructure.completedSection.isCompletedTh"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาไทย)"
              inputProps={{ disabled: isPublished || !isCompleteTh }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleCheckboxChange(e, 'Th')
              }
            />
            {isCompletedTh && dateTh && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                โดย {roleStampTh} {formattedCompletedSectionDateTh}
              </Typography>
            )}
          </Flex>

          <Flex flexDirection="column" alignItems={'center'}>
            <FormCheckbox
              sx={{ ml: 2 }}
              name="climateChangeStructure.completedSection.isCompletedEn"
              label="จัดเตรียมข้อมูลเรียบร้อย (ภาษาอังกฤษ)"
              inputProps={{ disabled: isPublished || !isCompleteEn }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleCheckboxChange(e, 'En')
              }
            />
            {isCompletedEn && dateEn && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                โดย {roleStampEn} {formattedCompletedSectionDateEn}
              </Typography>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default ClimateChangeStructureCompletedCheckbox;
